import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/index';
import { Textarea } from '@repo/videa-components/src/ui/textarea';
import { useAtom } from 'jotai';

import { mockAIImpactDataAtom } from '@/app/external/atoms';
import { useGetPracticeAIImpact } from '@/app/external/hooks';
import type {
  PracticeAIImpactData,
  PracticeAIImpactDataResponse,
} from '@/app/external/types';

import { JsonViewer } from './JsonViewer';
import { useEditableJson } from './useEditableJson';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const AIImpactJsonInputModal = ({ isOpen, onClose }: Props) => {
  const { t } = useLingui();
  const [mockData, setMockData] = useAtom(mockAIImpactDataAtom);

  // The hook already checks for mock data and returns it if it exists, otherwise fetches from API
  const { data: apiData } = useGetPracticeAIImpact({
    isEnabled: isOpen,
  });

  // Use editable JSON hook to manage edit mode logic
  // We work with transformed data (PracticeAIImpactData) - the JSON editor handles it gracefully
  const {
    isEditMode,
    jsonInput,
    error,
    enterEditMode,
    cancelEdit,
    applyEdit,
    clear,
    setJsonInput,
    setError,
    handleJsonEdit,
  } = useEditableJson<PracticeAIImpactData>({
    data: apiData ?? null,
    onSave: (data) => {
      // Store the transformed data - the hook will handle transformation when reading
      // The atom type expects PracticeAIImpactDataResponse, but we store transformed data
      // and the hook transforms it when reading from the atom
      setMockData(data as unknown as PracticeAIImpactDataResponse);
    },
    onClear: () => {
      setMockData(null);
      onClose();
    },
  });

  const isUsingMockAtom = !!mockData;
  const displayData: PracticeAIImpactData | null = apiData ?? null;
  const showEditMode = isEditMode || !displayData;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      cancelEdit();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="vai-max-w-5xl vai-max-h-[95vh] vai-flex vai-flex-col">
        <DialogHeader>
          <DialogTitle>
            <Trans>Mock AI Impact Data</Trans>
          </DialogTitle>
          <DialogDescription>
            {showEditMode ? (
              <Trans>Paste the JSON response from the AI Impact endpoint</Trans>
            ) : (
              <Trans>
                View and edit mock data. Changes are saved automatically.
              </Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="vai-flex vai-flex-col vai-gap-4 vai-flex-1 vai-min-h-0 vai-overflow-hidden">
          {showEditMode ? (
            <>
              <Textarea
                className="vai-min-h-[70vh] vai-h-[70vh] vai-font-mono vai-text-sm vai-bg-gray-50 vai-text-gray-900 vai-border-gray-300 vai-p-4 vai-leading-relaxed placeholder:vai-text-gray-400 focus-visible:vai-border-gray-400 focus-visible:vai-ring-gray-300 vai-resize-none vai-overflow-y-auto"
                placeholder={t`Paste JSON response here...`}
                value={jsonInput}
                onChange={(e) => {
                  setJsonInput(e.target.value);
                  setError(null);
                }}
              />

              {error && (
                <div className="vai-rounded-md vai-bg-red-50 vai-border vai-border-red-200 vai-p-3">
                  <p className="vai-text-sm vai-text-red-800 vai-font-medium">
                    {error}
                  </p>
                </div>
              )}
            </>
          ) : displayData ? (
            <JsonViewer
              src={displayData as object}
              onEdit={handleJsonEdit}
              onAdd={handleJsonEdit}
              onDelete={handleJsonEdit}
            />
          ) : null}

          {displayData && !showEditMode && (
            <div className="vai-rounded-md vai-bg-green-50 vai-border vai-border-green-200 vai-p-3">
              <p className="vai-text-sm vai-text-green-800 vai-font-medium">
                {isUsingMockAtom ? (
                  <Trans>
                    Mock data is currently active. Clear to use real API.
                  </Trans>
                ) : (
                  <Trans>
                    Viewing API data. Edit to create mock data from this.
                  </Trans>
                )}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} aria-label="Close AI Impact Modal">
            <Trans>Close</Trans>
          </Button>
          {displayData && !showEditMode && (
            <>
              {isUsingMockAtom && (
                <Button variant="outline" onClick={clear}>
                  <Trans>Clear Mock Data</Trans>
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => enterEditMode(displayData)}
              >
                <Trans>Edit Raw</Trans>
              </Button>
            </>
          )}
          {showEditMode && (
            <>
              {displayData && (
                <Button variant="outline" onClick={cancelEdit}>
                  <Trans>Cancel</Trans>
                </Button>
              )}
              <Button onClick={applyEdit}>
                <Trans>Apply</Trans>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
